// Career actions that span both stores (game + career). Kept out of the
// stores themselves so neither has to depend on the other at module load.

import { useGameStore, type Court } from '@/store/gameStore'
import { useCareerStore, type CareerClubRecord } from '@/store/careerStore'
import { EventBus, GameEvents } from '../utils/EventBus'
import {
  DIVISIONS, SEASON, LOWEST_DIVISION, HIGHEST_DIVISION, createSeason,
} from '@/config/leagueConfig'
import { generatePlayer, ROSTER_RULES } from '@/config/playersConfig'
import type { Building } from '@/config/buildingConfig'
import { type Staff, createStaff } from '@/config/staffConfig'
import { enrollInLeague } from './LeagueSystem'
import { defaultRng } from '../utils/rng'

/** Summarise the current club's run into an archive record */
function buildClubRecord(endedReason: CareerClubRecord['endedReason']): CareerClubRecord {
  const g = useGameStore.getState()
  const career = useCareerStore.getState()
  const history = g.leagueHistory
  // A "title" is only the top division championship; division wins are promotions
  const titles = history.filter(h => h.position === 1 && h.division === HIGHEST_DIVISION).length
  const promotions = history.filter(h => h.position <= SEASON.promotedCount && h.division > 1).length
  const relegations = history.filter(
    h => h.position > SEASON.clubsPerDivision - SEASON.relegatedCount && h.division < LOWEST_DIVISION
  ).length
  const bestDivision = history.length > 0 ? Math.min(...history.map(h => h.division)) : LOWEST_DIVISION

  return {
    clubName: g.managerClubName || career.currentClubName || 'Club anterior',
    seasonsManaged: history.length,
    titles,
    promotions,
    relegations,
    bestDivision,
    endedReason,
    leagueHistory: history,
  }
}

/**
 * Accept an offer to manage another club: archive the current club into the
 * career, then take over an already-built club — a starter roster, courts and
 * a league in the offered division. Preserves difficulty and the whole career
 * meta-layer. Returns false if the offer is gone or the game is already over.
 */
export function acceptClubOffer(offerId: string): boolean {
  const career = useCareerStore.getState()
  const g = useGameStore.getState()
  if (g.gameResult) return false // don't switch out of a finished game silently

  const offer = career.pendingOffers.find(o => o.id === offerId)
  if (!offer) return false

  // Archive the outgoing club, consume the offer, switch the career's club
  career.archiveCurrentClub(buildClubRecord('left'))
  career.acceptOffer(offerId)
  career.setCurrentClubName(offer.clubName)

  // Reset the club (keeps difficulty + careerStore)
  useGameStore.getState().startNewClubFromOffer(offer)

  // Seed an established club: enough courts to operate + a starter roster so
  // it can compete, and a league season in the offered division.
  const rng = defaultRng
  const courtCount = Math.max(3, Math.min(8, offer.startingClubLevel))
  const courts: Court[] = Array.from({ length: courtCount }, (_, i) => ({
    id: `court-takeover-${i}`,
    x: 0,
    y: 0,
    type: i % 3 === 2 ? 'indoor' : 'outdoor',
    condition: 100,
    pricePerHour: 20,
    isOccupied: false,
  }))
  const players = Array.from({ length: ROSTER_RULES.leagueMinPlayers }, (_, i) => ({
    ...generatePlayer(offer.startingClubLevel, 1, rng),
    id: `takeover-${i}`,
  }))
  const week = useGameStore.getState().week
  useGameStore.setState({
    courts,
    players,
    league: createSeason(offer.division, 1, week, rng),
  })

  EventBus.emit(GameEvents.CLUB_CHANGED, {
    clubName: offer.clubName,
    divisionName: DIVISIONS[offer.division].name,
  })
  return true
}

const COACH_START = {
  clubLevel: 3,
  reputation: 3.5,
  /** Transfer/wage budget the board hands the coach (club is already solvent) */
  budget: 35000,
} as const

/**
 * Set up a Coach career: you don't build a club, you're handed an established,
 * solvent one and focus on the squad + results. Seeds courts, key buildings,
 * a backroom staff (incl. a coach who develops players), a starter roster and
 * a league enrolment. The board sets the season objective on the next week.
 * Called from the menu after the standard new-game reset for gameMode 'coach'.
 */
export function startCoachCareer(): void {
  const rng = defaultRng

  const courts: Court[] = Array.from({ length: 4 }, (_, i) => ({
    id: `coach-court-${i}`,
    x: 0,
    y: 0,
    type: i === 3 ? 'indoor' : 'outdoor',
    condition: 100,
    pricePerHour: i === 3 ? 25 : 15,
    isOccupied: false,
  }))

  const buildings: Building[] = (['reception', 'cafe', 'locker'] as const).map((type, i) => ({
    id: `coach-bld-${i}`,
    type,
    x: 0,
    y: 0,
    level: 1,
    condition: 100,
  }))

  // A backroom staff: the coach drives player progression (the heart of the
  // mode); the rest keep the club running so the coach can focus on the squad.
  const staffSpec: Array<{ type: Staff['type']; skill: number }> = [
    { type: 'coach', skill: 74 },
    { type: 'maintenance', skill: 62 },
    { type: 'receptionist', skill: 60 },
    { type: 'barista', skill: 55 },
  ]
  const staff: Staff[] = staffSpec.map((s, i) =>
    createStaff(s.type, { day: 1, skill: s.skill, id: `coach-staff-${i}` }),
  )

  const players = Array.from({ length: ROSTER_RULES.leagueMinPlayers }, (_, i) => ({
    ...generatePlayer(COACH_START.clubLevel, 1, rng),
    id: `coach-player-${i}`,
  }))

  useGameStore.setState({
    courts,
    buildings,
    staff,
    players,
    clubLevel: COACH_START.clubLevel,
    reputation: COACH_START.reputation,
    money: COACH_START.budget,
    enabledServices: ['court_rental'],
  })

  // Enroll the club's pair in the league (lowest division — prove yourself)
  enrollInLeague(rng)
}
